import { getMessaging, onMessage, getToken } from 'firebase/messaging';

import { ROUTES } from 'services/utils/enums';
import { sendRequest } from 'services/api';

console.log('process.env.REACT_APP_VAPID_KEY', process.env.REACT_APP_VAPID_KEY);
const SERVER_VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

const PERMISSION_RESULTS = {
    GRANTED: 'granted',
    DENIED: 'denied',
};

class PushManager {
    constructor() {
        if (PushManager._instance) {
            return PushManager._instance;
        }

        PushManager._instance = this;

        this.notificationCallbacks = [];
    }

    async destroy() {
        await this.disable();
        this.notificationCallbacks.length = 0;
        delete PushManager._instance;
    }

    async initialize(serviceWorkerRegistration, app, notificationCallback) {
        this.app = app;
        this.serviceWorkerRegistration = serviceWorkerRegistration;

        this.addNotificationCallback(notificationCallback);

        this.broadcastChannel = new BroadcastChannel('sw-messages');
        this.broadcastChannel.addEventListener('message', event => {
            console.log('Received broadcast message:', event);

            if (event.data?.message === 'notification') {
                this.notificationCallbacks.forEach((callback) => {
                    if (typeof callback !== 'function') return;
                    callback({ ...event.data.payload, timestamp: Date.now() });
                });
            }
        });

        if ('serviceWorker' in navigator && 'Notification' in window && 'PushManager' in window) {
            // MTODO: If the user does not grant permission we still want to receive notifications from within the app, without push
            // Only enable FCM notifications if the user has granted permission, otherwise Firebase will throw an error:
            // An error occurred while retrieving token. FirebaseError: Messaging: The notification permission was not granted
            // and blocked instead. (messaging/permission-blocked).
            if (Notification.permission === PERMISSION_RESULTS.DENIED) {
                console.warn('Notification permission denied.');
                this.disable();
                return;
            }

            if (Notification.permission === PERMISSION_RESULTS.GRANTED) {
                console.log('Notification permission granted.');
                this.enable();
                return;
            }

            console.log('Requesting Notification permission...');
            Notification.requestPermission().then((permission) => {
                console.log('Notification permission:', permission);
                if (permission === PERMISSION_RESULTS.GRANTED) {
                    this.enable();
                } else {
                    this.disable();
                }
            });
        } else {
            console.warn('serviceWorker, Notification, or PushManager not yet configured');
        }
    }

    async enable() {
        console.log('Enabling push notifications...');

        await navigator.serviceWorker.ready;

        if (this.app) {
            const messaging = getMessaging(this.app);

            if (messaging) {
                try {
                    const token = await getToken(
                        messaging,
                        {
                            vapidKey: SERVER_VAPID_KEY,
                            serviceWorkerRegistration: this.serviceWorkerRegistration,
                        },
                    );

                    if (token) {
                        this.token = token;
                        // Send the subscription token to the server to use it to subscribe to FCM topics
                        await this.registerSubscription();

                        // Register a callback to handle messages received while the app is in the foreground
                        onMessage(messaging, async (pushObject) => {
                            console.log('FCM Notification received:', pushObject);

                            if (pushObject?.notification?.title) {
                                this.notificationCallbacks.forEach((callback) => {
                                    if (typeof callback !== 'function') return;
                                    callback({ ...pushObject, timestamp: Date.now() });
                                });
                            }
                        });
                    } else {
                        console.error('No registration token available.');
                    }
                }
                catch (err) {
                    console.error('An error occurred while retrieving a token: ', err);
                }
            }
        }
    }

    async disable() {
        console.log('Disabling push notifications...');

        await this.unregisterSubscription();
    }

    addNotificationCallback(callback) {
        if (typeof callback !== 'function') return;
        this.notificationCallbacks.push(callback);
    }

    async registerSubscription() {
        try {
            const token = this.token;
            console.log('Registering subscription for device:', token);

            const pushRegistrationRes = await sendRequest(ROUTES.SUBSCRIBE, '', { token });

            if (pushRegistrationRes?.status !== 200 && pushRegistrationRes?.status !== 201) {
                console.log('Error while registering subscription:', pushRegistrationRes);
                throw new Error(`Error while registering subscription. ${pushRegistrationRes.data.msg || ''}`);
            }

            console.log('Subscription registered successfully:', pushRegistrationRes);
        } catch (err) {
            console.error('Error while registering subscription:', err);
        }
    }

    async unregisterSubscription() {
        try {
            const token = this.token;
            console.log('Unregistering subscription for device:', token);

            const res = await sendRequest(ROUTES.UNSUBSCRIBE, '', { token });

            if (res?.status !== 200 && res?.status !== 201) {
                throw new Error(`Error while registering subscription. ${res.data.msg || ''}`);
            }

            console.log('Subscription unregistered successfully:', res);
        } catch (err) {
            console.error('Error while unregistering subscription:', err);
        }
    }
}

PushManager._instance = undefined;

export default new PushManager();
